/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import QSModal from '../modal';
import { QSButton, QSIconButton, QSTypography } from '../../atoms';
import { Close } from '@mui/icons-material';
import { upgradeSSOAtom } from '../../../../modules/home/v1/states/upgrade-plan';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import ProgressiveImage from '../../atoms/progressive-image';
import SSOIntegrationImg from '../../../assets/img/ChannelPartner/SSOIntegration.webp';
import { getUserChannelData, shouldHideDTDetails } from '../../../../modules/login/v1/utility';
import useNotifications from '../../../hooks/use-notifications';
import SingleTickIcon from '../../../icons/SingleTick';
import UpArrowIcon from '../../../icons/UpArrow';
import { openPopup } from '../../../utils/popup';

const Features = [
  'One-click logins save time and eliminate OTP fatigue',
  'Easy user provisioning and compliance auditing',
  'Reduce risks with centralized authentication',
  'Connect using Google Workspace, Microsoft Entra ID or Okta',
];

const SSOUpgradeModal = () => {
  const [upgradeModal, setUpgradeModal] = useAtom(upgradeSSOAtom);
  const channelData = getUserChannelData();
  const hideDTDetails = shouldHideDTDetails();
  const orgName = channelData?.name ?? 'DoubleTick';
  const { addNotification } = useNotifications();

  const onClose = useCallback(() => {
    setUpgradeModal({
      isModalActive: false,
    });
  }, [setUpgradeModal]);

  let contactUsLink = 'https://wa.me/918356849474?text=I%27m%20interested%20in%20DoubleTick%20Enterprise';
  if (hideDTDetails) {
    contactUsLink = `https://wa.me/${channelData?.supportNumber}?text=I%27m%20interested%20in%20Enterprise%20Plan`;
  }

  const onClickContactUs = useCallback(async () => {
    try {
      openPopup(contactUsLink, '_blank');
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred. Please try again',
        type: 'error',
      });
    }
  }, [addNotification, onClose]);

  if (!upgradeModal.isModalActive) {
    return null;
  }

  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={upgradeModal.isModalActive}
      onClose={onClose}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          border-radius: 4px;
          overflow: hidden;
          background: white;
          outline: none;
          position: relative;
          padding: 16px;
          max-width: 700px;
        `}
      >
        <QSIconButton
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
            box-shadow: none;
          `}
          onClick={onClose}
        >
          <Close />
        </QSIconButton>
        <div
          css={css`
            flex: 1;
            overflow: auto;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 20px;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex: 1;
              background-color: #f6f1e7;
              border-radius: 8px;
              overflow: hidden;
            `}
          >
            <ProgressiveImage imgProps={{ width: '100%', height: '400px' }} src={SSOIntegrationImg} />
          </div>
          <div
            css={css`
              flex: 1;
              padding: 12px 0 12px 20px;
              display: flex;
              flex-direction: column;
              gap: 20px;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 16px;
              `}
            >
              <span
                css={css`
                  width: fit-content;
                  border: 1px solid #e0e0e9;
                  border-radius: 50px;
                  padding: 4px 8px;
                  display: flex;
                  gap: 8px;
                  align-items: center;
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                <UpArrowIcon />
                <span>Upgrade to {orgName} Enterprise plan</span>
              </span>
              <QSTypography
                css={css`
                  font-weight: 600;
                  padding-right: 12px;
                  font-size: 20px;
                  overflow: hidden;
                  line-height: 28px;
                `}
              >
                Enable Single Sign on (SSO){' '}
                <span
                  css={css`
                    color: #7a7a7a;
                  `}
                >
                  {' '}
                  for Faster login and centralised management of your team's details
                </span>
              </QSTypography>

              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                `}
              >
                {Features.map((feature, index) => (
                  <div
                    key={index}
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 8px;
                    `}
                  >
                    <SingleTickIcon width={20} height={20} color={'#3f906d'} />
                    <span
                      css={css`
                        font-size: 12px;
                        color: #2d2d2d;
                        font-weight: 500;
                      `}
                    >
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 8px;
              `}
            >
              <QSButton
                css={css`
                  height: 40px;
                  width: 100%;
                `}
                onClick={onClickContactUs}
              >
                Upgrade to Enterprise Plan
              </QSButton>
            </div>
          </div>
        </div>
      </div>
    </QSModal>
  );
};

export default SSOUpgradeModal;
